import clsx from "clsx";
import { ReactNode } from "react";

interface FieldRowProps {
  dataTestid?: string;
  className?: string;
  label: ReactNode;
  icon?: ReactNode;
  value?: ReactNode;
  disabled?: boolean;
}

export const FieldRow = ({
  dataTestid,
  className,
  label,
  icon,
  value,
  disabled = false,
}: FieldRowProps) => {
  const isEmpty = value === undefined || value === null || value === "";
  const textColor = disabled
    ? "text-foreground-tertiary"
    : "text-foreground-primary";

  return (
    <div
      data-testid={dataTestid}
      className={clsx(
        // py-3.5 and border-2 (transparent) match the editable stacked
        // FormField's own box model (FormField.scss's
        // `.form-field-container--stacked`) exactly - that rule's min-h-14
        // floor doesn't actually cap real 2-line label+value content at
        // 64px once its padding and reserved border are added, it settles
        // ~4px taller. Matching the same recipe here keeps this read-only
        // row the same height as its editable counterpart instead of a
        // jump on toggle.
        "flex min-h-14 min-w-0 items-center justify-between gap-3 rounded border-2 border-transparent bg-surface-tertiary px-5 py-3.5",
        disabled && "bg-surface-quaternary",
        className,
      )}
    >
      {isEmpty ? (
        // Empty state: only the label, at value size, vertically centered
        <span
          className={clsx(
            "truncate text-base font-semibold leading-md",
            textColor,
          )}
        >
          {label}
        </span>
      ) : (
        <div className="flex min-w-0 flex-col">
          <span className={clsx("text-xs font-semibold leading-md", textColor)}>
            {label}
          </span>
          <span
            className={clsx(
              "truncate text-base font-semibold leading-md",
              textColor,
            )}
            title={
              typeof value === "string" || typeof value === "number"
                ? String(value)
                : undefined
            }
          >
            {value}
          </span>
        </div>
      )}
      {icon}
    </div>
  );
};
